import { useState, useEffect, useCallback } from 'react';
import {
  getAdminNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  generateIntelligentNotifications
} from '../services/notificationService';
import { useNotificationAnalytics } from './useAnalytics';

/**
 * Hook for managing admin notifications
 * @param {number} [maxCount=10] - Maximum number of notifications to fetch
 * @param {boolean} [includeIntelligent=true] - Include intelligent notifications from analytics
 * @returns {Object} - { notifications, unreadCount, loading, error, markAsRead, markAllAsRead, deleteNotif, refetch }
 */
export const useNotifications = (maxCount = 10, includeIntelligent = true) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get notification analytics for intelligent notifications
  const { data: notificationAnalytics } = useNotificationAnalytics('day');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch real notifications from Firestore
      const realNotifications = await getAdminNotifications(maxCount, false);
      
      // Fetch unread count
      const unread = await getUnreadNotificationCount();
      setUnreadCount(unread);

      // Generate intelligent notifications from analytics
      let intelligentNotifications = [];
      if (includeIntelligent && notificationAnalytics) {
        intelligentNotifications = generateIntelligentNotifications({
          notificationData: notificationAnalytics
        });

        // Mark intelligent notifications with a special flag
        intelligentNotifications = intelligentNotifications.map(notif => ({
          ...notif,
          id: `intelligent-${Date.now()}-${Math.random()}`,
          isIntelligent: true,
          read: false // Intelligent notifications start as unread
        }));
      }

      // Combine real and intelligent notifications
      const allNotifications = [...intelligentNotifications, ...realNotifications];

      // Sort by priority and date
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      allNotifications.sort((a, b) => {
        const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setNotifications(allNotifications.slice(0, maxCount));
    } catch (err) {
      console.error('❌ Error fetching notifications:', err);
      setError(err.message);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [maxCount, includeIntelligent, notificationAnalytics]);

  useEffect(() => {
    fetchNotifications();
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      // Only mark real notifications as read in Firestore
      if (!notificationId.startsWith('intelligent-')) {
        await markNotificationAsRead(notificationId);
      }

      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, read: true, readAt: new Date().toISOString() }
            : notif
        )
      );

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('❌ Error marking notification as read:', err);
      throw err;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead();
      
      // Update local state
      setNotifications(prev =>
        prev.map(notif => ({
          ...notif,
          read: true,
          readAt: new Date().toISOString()
        }))
      );

      setUnreadCount(0);
    } catch (err) {
      console.error('❌ Error marking all notifications as read:', err);
      throw err;
    }
  }, []);

  const deleteNotif = useCallback(async (notificationId) => {
    try {
      // Only delete real notifications from Firestore
      if (!notificationId.startsWith('intelligent-')) {
        await deleteNotification(notificationId);
      }

      // Remove from local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      
      // Update unread count if it was unread
      const deletedNotif = notifications.find(n => n.id === notificationId);
      if (deletedNotif && !deletedNotif.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('❌ Error deleting notification:', err);
      throw err;
    }
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotif,
    refetch: fetchNotifications
  };
};

